import { AssignmentItem } from '@components/activities/AssignmentActionButtons';
import {
    useGradeSubmissionMutation,
    useOverrideActivityGradeMutation,
    useOverrideActivityStatusMutation,
    SingleSubmission,
    ActivityStatusOverride,
    SubmissionStatus,
} from '@graphql/generated-graphql';
import { getClient } from '@graphql/graphql-client/graphClient';
import { useInvalidateQueries } from '@hooks/useInvalidateQueries';
import * as StatusSVG from '@components/common/svgs/StatusIconSVGs';
import { CheckIcon } from '@heroicons/react/outline';
import { useQueryClient } from 'react-query';
import { useGradebookDispatch } from '@components/context/GradebookContext';

const NOOP = () => {};

export function useGradeSubmissions(
    {
        onSuccess = NOOP,
        onError = NOOP,
        gradebook = false,
    }: {
        onSuccess?: () => void;
        onError?: () => void;
        gradebook?: boolean;
    } = { onSuccess: NOOP, onError: NOOP },
) {
    const invalidate = useInvalidateQueries();
    const queryClient = useQueryClient();
    const {resetGradebookData} = useGradebookDispatch();
    const gradeMutation = useOverrideActivityGradeMutation(getClient('courses'), {
        onMutate: () => {
            if (gradebook)
                queryClient.cancelQueries(['gradebookActivitiesData', 'gradebookActivitiesUpdatedData', 'gradebookStudentsAvgData']);
        },
        onSettled: async (data) => {
            if (data?.overrideActivityGrade?.success) {
                if (gradebook) {
                    // resetGradebookData(true);
                    invalidate('gradebookActivitiesUpdatedData');
                    invalidate('gradebookStudentsAvgData');
                } else {
                    invalidate('CoursesActivitySubmissionsByEnrollments')
                    // resetGradebookData(true);
                } 
                onSuccess?.();
            } else {
                onError?.();
            }
        },
    });

    const activityOverride = useOverrideActivityStatusMutation(getClient('courses'), {
        onMutate: () => {
            if (gradebook)
                queryClient.cancelQueries(['gradebookActivitiesData', 'gradebookActivitiesUpdatedData', 'gradebookStudentsAvgData']);
        },
        onSettled: async (data) => {
            if (data?.overrideActivityStatus?.success) {
                if (gradebook) {
                    // resetGradebookData(true);
                    invalidate('gradebookActivitiesUpdatedData');
                    invalidate('gradebookStudentsAvgData');
                } else {
                    invalidate('CoursesActivitySubmissionsByEnrollments')
                    // resetGradebookData(true);
                } 
                onSuccess?.();
            } else {
                onError?.();
            }
        },
    });

    function handleGrading({
        activity,
        submission,
        enrollment,
        grade,
        oldGrade,
        status
    }: { activity: AssignmentItem; grade: number | null; oldGrade?: number | null, status?: string } & NonNullable<SingleSubmission>) {
        const submissionId = submission?.id;
        const enrollmentId = enrollment?.id;
        const activityId = activity?.id;

        const statusMap = {
            'skipped': 's',
            'absent': 'a',
            'dropped': 'd',
            'excused': 'e'
        }

        // const hasStatus = !!status && !!statusMap[status]
        const isGradeUpdated = `${grade}` !== `${submission?.grade}`

        const newGradeWithSubmission = !!grade && !!submission && isGradeUpdated;
        const test = oldGrade === 0 ? `${grade}` !== `${oldGrade}` : newGradeWithSubmission;
        const isNewGrade = !!oldGrade ? `${grade}` !== `${oldGrade}` : !!grade;
        const noOldGradeProvided = !oldGrade && `${grade}` !== `${submission?.grade}` && !!submission


        // const isNewGradeA = `${grade}` !== `${submission?.grade}`;
        // console.log('isNewGradeA', isNewGradeA)

        // console.log('grade', grade)
        const submitGrade = isNewGrade || test || noOldGradeProvided
        // console.log('-------------------------------------')
        // console.log('noOldGradeProvided', noOldGradeProvided)
        // console.log('isNewGrade', isNewGrade)
        // console.log('test', test)
        // console.log('newGradeWithSubmission', newGradeWithSubmission)
        // console.log('submission grade', submission?.grade)
        // console.log('submitGrade', submitGrade);
        // console.log('oldGrade', oldGrade)
        // console.log('-------------------------------------')

        const isNum = isNumber(grade);
        const statusOverride = letterToStatus(`${grade || ''}`);
        switch (true) {
            case grade === null:
                // console.log('HERE')
                if (submitGrade) {
                    gradeMutation.mutate({
                        id: activity?.id,
                        enrollmentId: enrollment?.id,
                        points: null,
                    });
                }
                break;
            case isNum && submitGrade:
                gradeMutation.mutate({
                    id: activity?.id,
                    enrollmentId: enrollment?.id,
                    points: `${grade}` === '' ? null : Number(grade),
                });
                break;
            case statusOverride !== null:
                activityOverride.mutate({
                    id: activityId,
                    enrollmentId: enrollmentId,
                    status: statusOverride,
                });
                break;
            default:
                break;
        }
    }

    function overrideActivityStatus({
        activity,
        submission,
        enrollment,
        statusOverride,
        onSuccess,
    }: {
        activity: AssignmentItem;
        statusOverride: ActivityStatusOverride;
        onSuccess?: () => void;
    } & NonNullable<SingleSubmission>) {
        const enrollmentId = enrollment?.id;
        const activityId = activity?.id;
        activityOverride.mutate(
            {
                id: activityId,
                enrollmentId: enrollmentId,
                status: statusOverride,
            },
            {
                onSettled: (data) => {
                    if (data?.overrideActivityStatus?.success) {
                        onSuccess?.();
                    }
                },
            },
        );
    }

    const isLoading = activityOverride?.isLoading || gradeMutation?.isLoading;

    return [handleGrading, isLoading, overrideActivityStatus] as const;
}

function isNumber(value: string | number) {
    return !isNaN(Number(value));
}

export function letterToStatus(text: string) {
    const letter = text?.toLowerCase()?.trim() ?? '';
    switch (letter) {
        case 'e':
            return ActivityStatusOverride.Excused;
        case 'd':
            return ActivityStatusOverride.Dropped;
        case 'r':
            return ActivityStatusOverride.Reassigned;
        case 's':
            return ActivityStatusOverride.Skipped;
        case 'm':
            return null;
        case 'a':
            return ActivityStatusOverride.Absent;
        default:
            return null;
    }
}

//
const cssStyle = {
    textAlign: 'center',
    borderRadius: '$2',
    paddingLeft: 8,
    paddingRight: 8,
    py: '$1',
    backgroundColor: '$gray100',
    color: '$gray500',
    px: '8px',
    fontWeight: 700,
    fontSize: '$1',
    fontFamily: '$inter',
    textTransform: 'capitalize',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    'column-gap': '4px',
};

export function getMarkDoneCss() {
    return {
        ...cssStyle,
        backgroundColor: '$white',
        color: '$lime600',
        border: '1px solid $lime600',
        cursor: 'pointer',
    };
}

export function getLateStatusCss() {
    return {
        ...cssStyle,
        backgroundColor: '$mango100',
        color: '$mango700',
        cursor: 'pointer',
    };
}

const largeStyle = {
    minHeight: '48px',
    fontSize: '$3',
};

function combineStyles(isLarge: boolean) {
    if (isLarge) {
        return { ...cssStyle, ...largeStyle };
    }

    return { ...cssStyle };
}

export function statusToCss(status: SubmissionStatus, isLarge?: boolean) {
    switch (status) {
        case SubmissionStatus.Excused:
            return { ...combineStyles(isLarge), backgroundColor: '$gray100', color: '$gray500' };
        case SubmissionStatus.Dropped:
            return { ...combineStyles(isLarge), backgroundColor: '$gray100', color: '$gray500' };
        case SubmissionStatus.Reassigned:
            return {
                ...combineStyles(isLarge),
                backgroundColor: '$teal100',
                color: '$teal500',
            };
        case SubmissionStatus.Skipped:
            return { ...combineStyles(isLarge), backgroundColor: '$ocean100', color: '$ocean700' };
        case SubmissionStatus.Absent:
            return {
                ...combineStyles(isLarge),
                backgroundColor: '$violet100',
                color: '$violet800',
            };
        case SubmissionStatus.Missed:
            return { ...combineStyles(isLarge), backgroundColor: '$red100', color: '$red700' };
        case 'late':
            return { ...combineStyles(isLarge), backgroundColor: '$mango100', color: '$mango700' };
        case SubmissionStatus.MarkedDone:
            return { ...combineStyles(isLarge), backgroundColor: '$lime100', color: '$lime700' };
        case SubmissionStatus.NeedsGrading:
            return {
                ...combineStyles(isLarge),
                backgroundColor: '$gray100',
                color: '$gray500',
                textTransform: 'lowercase',
                fontWeight: 500,
            };
        default:
            return {};
    }
}

export function statusToIcon(status: SubmissionStatus) {
    switch (status) {
        case SubmissionStatus.Excused:
            return StatusSVG.ExcusedStatus;
        case SubmissionStatus.Dropped:
            return StatusSVG.ExcusedStatus;
        case SubmissionStatus.Reassigned:
            return StatusSVG.ReassignStatus;
        case SubmissionStatus.Absent:
            return StatusSVG.IncompleteStatus;
        case SubmissionStatus.Missed:
            return StatusSVG.MissedStatus;
        case SubmissionStatus.Skipped:
            return StatusSVG.SkippedStatus;
        case 'late':
            return StatusSVG.LateStatus;
        case SubmissionStatus.MarkedDone:
            return CheckIcon;
        default:
            return null;
    }
}
